import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  List,
  CircularProgress,
  ClickAwayListener,
  Popper
} from '@material-ui/core';
import { useInView } from 'react-intersection-observer';

import SText from '../sui/stext';
import SLabel from '../sui/slabel';
import { AddLabel } from './components';
import SHighlightText from '../shighlight-text';
import { StyledItem, StyledMenu, StyledTextField } from './style';
import { SAutocompleteItem, SAutocompleteProps } from './interfaces';

const SAutocomplete = ({
  name,
  label,
  error,
  options,
  loading: isLoading,
  selected,
  disabled,
  required,
  helperText,
  placeholder,
  disableCreate,

  handleInputChange,
  handleItemClick,
  fetchMore
}: SAutocompleteProps) => {
  const { ref, inView } = useInView();
  const anchorRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');
  const [_, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [openMenu, setMenuOpen] = useState(false);

  useEffect(() => {
    if (inView) fetchMore?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
      handleInputChange(search);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [handleInputChange, search]);

  useEffect(() => {
    setLoading(prev => prev && isLoading);
  }, [isLoading]);

  const onClick = (item: SAutocompleteItem) => {
    handleItemClick(item);
    setMenuOpen(false);
    setSearch('');
    handleInputChange('');
    setLoading(false);
  };

  const showAddLabel = useMemo(
    () =>
      !disableCreate &&
      !selected?.find(elem => elem?.name === search) &&
      !options?.find(elem => elem?.name === search) &&
      !!search,
    [disableCreate, options, search, selected]
  );

  const showMenu = useMemo(
    () => openMenu && (!!options?.length || showAddLabel),
    [options, openMenu, showAddLabel]
  );

  return (
    <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
      <Box width="100%" position="relative">
        {Boolean(label) && (
          <SLabel
            title={label}
            variant="body2"
            required={required}
            style={{
              marginBottom: 8
            }}
          />
        )}

        <div ref={anchorRef} style={{ width: '100%' }}>
          <Box width="100%" onClick={() => setMenuOpen(true)}>
            <StyledTextField
              fullWidth
              name={name}
              error={error}
              variant="outlined"
              value={search}
              disabled={disabled}
              helperText={helperText}
              placeholder={placeholder}
              onFocus={() => setMenuOpen(true)}
              id={`autocomplete-${name}`}
              InputProps={{
                endAdornment: loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null,
                // Suppress the browser's native autofill/suggestion dropdown.
                // Chrome ignores autoComplete="off" for contact-like fields, so
                // the unrecognized "new-password" token is set on the actual
                // <input> to reliably disable it across browsers.
                inputProps: {
                  autoComplete: 'new-password',
                  autoCorrect: 'off',
                  autoCapitalize: 'off',
                  spellCheck: false
                }
              }}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (!openMenu) setMenuOpen(true);
                setLoading(true);
                setSearch(event.target.value);
              }}
            />
          </Box>

          {/* Portaled to the body so the menu is never clipped by an
              overflow/height-constrained ancestor (e.g. a dialog), and sits
              above modals. Width tracks the input via the anchor. */}
          <Popper
            open={showMenu}
            anchorEl={anchorRef.current}
            placement="bottom-start"
            style={{
              zIndex: 1400,
              width: anchorRef.current?.clientWidth
            }}
            modifiers={{
              flip: { enabled: true },
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport'
              }
            }}>
            <StyledMenu>
              {loading ? (
                <SText color="inherit" style={{ padding: 12 }}>
                  Loading...
                </SText>
              ) : (
                <Box display="flex" flexDirection="column" overflow="hidden">
                  <List
                    style={{
                      maxHeight: 150,
                      overflow: 'auto'
                    }}>
                    {!!options?.length &&
                      options?.map((item, index) => (
                        <StyledItem
                          button
                          key={item.id}
                          selected={selected?.includes(item)}
                          onClick={() => onClick(item)}
                          ref={index + 1 === options.length ? ref : null}>
                          <Box
                            display="flex"
                            alignItems="center"
                            style={{ minWidth: 0, gap: 10, width: '100%' }}>
                            {!!item.icon && (
                              <Box style={{ flexShrink: 0, display: 'flex' }}>
                                {item.icon}
                              </Box>
                            )}
                            <Box
                              display="flex"
                              flexDirection="column"
                              style={{ minWidth: 0, gap: 2 }}>
                              <SHighlightText
                                weight="bold"
                                color="inherit"
                                maxLines={1}
                                value={item.name}
                                searchQuery={search}
                              />
                              {!!item.subtitle && (
                                <SHighlightText
                                  size="sm"
                                  maxLines={1}
                                  fontColor="#5D7083"
                                  value={item.subtitle}
                                  searchQuery={search}
                                />
                              )}
                            </Box>
                          </Box>
                        </StyledItem>
                      ))}
                  </List>
                  {showAddLabel && (
                    <AddLabel
                      label={search || ''}
                      hasOptions={!!options?.length}
                      handleNewOption={(itemName: string, id: string) =>
                        onClick({
                          id,
                          name: itemName
                        })
                      }
                    />
                  )}
                </Box>
              )}
            </StyledMenu>
          </Popper>
        </div>
      </Box>
    </ClickAwayListener>
  );
};

export default SAutocomplete;
